import React, { useEffect, useState } from "react";
import { ClientApiProps } from "../../common";
import {
  ErrorScope,
  Label,
  Modal,
  Select,
  Spinner,
  TextInput,
  useValidatedState,
} from "acma-spa";
import { AlphaTagPayload, IMNO, ISettings, getApiClient } from "../../api";
import { AlphaTagStore } from "../../stores/AlphaTagStore";
import * as yup from "yup";

export interface AlphaTagModalProps extends ClientApiProps {
  open: boolean;
  mnos: IMNO[];
  settings: ISettings[];
  onClose: () => any;
  onChange: () => Promise<any>;
}

interface Model {
  abn: string | null;
  mnoId: string | null;
  names: string[];
  acknowledgement: boolean;
}

const schema: yup.ObjectSchema<Model> = yup.object({
  abn: yup.string().required().label("Registrant"),
  mnoId: yup.string().defined().nullable("MNO"),
  names: yup
    .array()
    .defined()
    .test(
      "is-permitted",
      "At least 3 and at most 11 characters. Permitted for use - 'A-Z', 'a-z', '0-9', '.', '-', '_', '&'. At least one 'A-Z' or 'a-z'.",
      (names) =>
        names.map((r) => r.trim()).filter((r) => r.length > 0 && new RegExp(/^(?=[0-9_&.-\s]*[A-Za-z])[A-Za-z0-9_&.-\s]{3,11}$/).test(r)).length === names.filter(r => r.length > 0).length
    )
    .test(
      "is-notempty",
      "At least one name is required",
      (names) =>
        names.map((r) => r.trim()).filter((r) => (r ? true : false)).length > 0
    ),
  acknowledgement: yup.boolean().defined(),
});

export const AlphaTagModal: React.FC<AlphaTagModalProps> = (props) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [spinner, setSpinner] = useState<string>("");
  const [model, setModel, errors, validate] = useValidatedState<Model>(
    {
      abn: null,
      mnoId: null,
      names: [""],
      acknowledgement: false,
    },
    schema
  );

  useEffect(() => {
    if (props.settings.length == 1) {
      setModel({ abn: props.settings[0].clientAbn });
    }
  }, [props.settings])


  const onNameChange = (i?: number, v?: string) => {
    var newNames = model.names.slice();
    console.log(newNames)
    if (i != undefined) {
      if (v != undefined) newNames.splice(i, 1, v);
      else newNames.splice(i, 1);
    } else {
      newNames.push("");
    }
    setModel({ names: newNames });

    console.log(v)
  };

  const onAdd = async () => {
    var res = await validate("#alphaTagContainer");
    if (!res) {
      return;
    }

    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );
    const store = new AlphaTagStore(client);

    setSpinner("Saving...");
    setError("");

    try {
      await store.addAlphaTags(
        new AlphaTagPayload({
          clientAbn: model.abn!,
          mnoId: model.mnoId ?? undefined,
          names: model.names,
        })
      );
      await props.onChange();
      props.onClose();
    } catch (e: any) {
      setSpinner("");
      setError(e.message ? e.message : "Unexpected error");
    }
  };

  return (
    <>
      {props.open ? (
        <Modal
          id="alphaTagModal"
          size="lg"
          open={true}
          onOpened={() => document.getElementById("abn")?.focus()}
          onClose={() => !spinner && props.onClose()}
          title="Add new alpha tags"
          keyboard={false}
          backdrop="static"
          leftButtons={[
            {
              label: "Add",
              id: "add",
              onClick: () => onAdd(),
              disabled: spinner ? true : false,
              title: "",
            },
            {
              label: "Cancel",
              id: "cancel",
              onClick: () => props.onClose(),
              mode: "secondary",
            },
          ]}
        >
          <div id="alphaTagContainer" className="mb-2">
            <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
              <Label
                id="abn"
                label="Registrant"
                error={errors.abn}
                required
              >
                <Select<string>
                  value={model.abn}
                  onChange={v => setModel({ abn: v ?? undefined })}
                  options={props.settings.map(r => ({ value: r.clientAbn ?? "", label: r.clientName ?? "" }))}
                  defaultLabel={props.settings.length != 1 ? "Select..." : undefined}
                  size="block"
                />
              </Label>

              <Label id="companyName" label="SMS service provider" className="mt-3" hint={<>
                <span>Please specify an SMS service provider if you intend to use them and only them to send SMS messages from the alpha tags you are registering. Otherwise, please leave this field empty.</span>
              </>}>
                <Select<string> options={props.mnos as any} value={model.mnoId} onChange={v => setModel({ mnoId: v })} defaultLabel="Select..." size="block" />
              </Label>

              <Label
                id="alphaTag"
                label="Alpha tag"
                className="mt-3"
                error={errors.names as string}
                hint="At least 3 and at most 11 characters. Permitted for use - 'A-Z', 'a-z', '0-9', '.', '-', '_', '&'. At least one 'A-Z' or 'a-z'."
                required
              />

              {model.names.map((v, i) => {
                return (
                  <div
                    className="row no-gutters"
                    key={i}
                    style={{ marginTop: 11 }}
                  >
                    <div className="col">
                      <TextInput
                        id={`name${i > 0 ? `-${i}` : ""}`}
                        value={v}
                        onChange={(v) => onNameChange(i, v)}
                        size="block"
                        maxLength={400}
                      />
                    </div>
                    <div className="col col-auto p-2">
                      <a
                        id={`remove${i > 0 ? `-${i}` : ""}`}
                        href="#"
                        style={{ visibility: i == 0 ? "hidden" : "visible" }}
                        onClick={(e) => {
                          e.preventDefault();
                          onNameChange(i);
                        }}
                      >
                        Remove
                      </a>
                    </div>
                  </div>
                );
              })}
              <div className="mt-2">
                <a
                  id="another"
                  href="#"
                  onClick={(e) => {
                    console.log('a')
                    e.preventDefault();
                    onNameChange();
                  }}
                  style={{
                    visibility: model.names.length < 10 ? "visible" : "hidden",
                  }}
                >
                  Add another alpha tag
                </a>
              </div>   
            </Spinner>
          </div>
          <ErrorScope id="error" error={error} />
        </Modal>
      ) : (
        <></>
      )}
    </>
  );
};
